import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Lead } from "../types";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { LeadDetailsForm } from "./lead/LeadDetailsForm";
import { LeadDialogFooter } from "./lead/LeadDialogFooter";

interface ConvertLeadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  lead: Lead;
  onConvert: () => void;
}

export const ConvertLeadDialog = ({
  isOpen,
  onClose,
  lead,
  onConvert
}: ConvertLeadDialogProps) => {
  const [editedLead, setEditedLead] = useState<Lead>(lead);
  const { toast } = useToast();

  const handleInputChange = (field: keyof Lead, value: string | number) => {
    setEditedLead((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUpdate = async () => {
    try {
      const { error } = await supabase
        .from('quote_leads')
        .update({
          first_name: editedLead.first_name,
          last_name: editedLead.last_name,
          email: editedLead.email,
          phone: editedLead.phone,
          insurance_type: editedLead.insurance_type,
          quoted_amount: editedLead.quoted_amount,
          pipeline_stage: editedLead.pipeline_stage,
          street: editedLead.street,
          city: editedLead.city,
          state: editedLead.state,
          zip_code: editedLead.zip_code,
          lead_source: editedLead.lead_source
        })
        .eq('id', lead.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Lead updated successfully",
      });
      
      onConvert();
    } catch (error: any) {
      console.error('Error updating lead:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update lead",
      });
    }
  };

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from('quote_leads')
        .delete()
        .eq('id', lead.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Lead deleted successfully",
      });
      
      onClose();
      onConvert(); // This will refresh the leads list
    } catch (error: any) {
      console.error('Error deleting lead:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete lead",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Lead Details</DialogTitle>
          <DialogDescription>
            View and edit lead information or convert to customer
          </DialogDescription>
        </DialogHeader>
        
        <LeadDetailsForm 
          editedLead={editedLead}
          handleInputChange={handleInputChange}
        />

        <DialogFooter>
          <LeadDialogFooter
            onClose={onClose}
            onDelete={handleDelete}
            onUpdate={handleUpdate}
            onConvert={onConvert}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};